/**
 * Settings > Case Preparation > Case Insights: what Case Insights does on its
 * own on every case at the firm (`case_insights_automation`). Each case's
 * Case Insights > On This Case turns single insights or memos off there.
 * Reading the petitioner's website isn't a setting: a save leaves
 * `petitioner_web` out, so the backend's default (on) applies.
 */
import type { FC } from "react";

import {
  casePrepPatch,
  casePrepValue,
  hasCasePrepField,
  type CaseInsightsAutomation
} from "@/api/types/lawFirm";
import { Switch } from "@/components/ui/switch";

import {
  CasePrepRow,
  CasePrepSection,
  NeedsBackendNotice
} from "./casePrepParts";
import { useFirmSave, type SectionProps } from "./FirmHouseStyleSection";

export const CASE_INSIGHTS_ANCHOR = "case-insights";

/** What a firm that never changed these gets. */
export const CASE_INSIGHTS_DEFAULTS: CaseInsightsAutomation = {
  keep_current: true,
  run_analyses: false,
  petitioner_web: true
};

type ShownSwitch = "keep_current" | "run_analyses";

const SWITCHES: ReadonlyArray<{
  id: ShownSwitch;
  title: string;
  description: string;
}> = [
  {
    id: "keep_current",
    title: "Keep Case Insights Current",
    description:
      "Recompute insights when documents or case details change. Off, they update when someone rechecks."
  },
  {
    id: "run_analyses",
    title: "Write Research Memos Automatically",
    description:
      "Write each research memo once its facts are ready. Off, memos are written only when asked for: the Case Research workflow, a draft that argues from one, or Run Again."
  }
];

export const CaseInsightsSection: FC<SectionProps> = ({
  lawFirmId,
  firm,
  canEdit,
  onSaved
}) => {
  const { saving, save } = useFirmSave({ lawFirmId, onSaved });
  const supported = hasCasePrepField(firm, "caseInsightsAutomation");
  const current: CaseInsightsAutomation = {
    ...CASE_INSIGHTS_DEFAULTS,
    ...casePrepValue(firm, "caseInsightsAutomation")
  };

  const set = (id: ShownSwitch, on: boolean) =>
    void save(
      casePrepPatch("caseInsightsAutomation", {
        keep_current: current.keep_current,
        run_analyses: current.run_analyses,
        [id]: on
      }),
      "Case Insights defaults saved"
    );

  return (
    <CasePrepSection
      title="Case Insights"
      description="What Case Insights does on its own, on every case. A case's On This Case tab turns single insights or memos off there."
      testId="case-prep-case-insights"
    >
      {SWITCHES.map((s) => (
        <CasePrepRow
          key={s.id}
          title={s.title}
          description={s.description}
          testId={`case-insights-${s.id}`}
          control={
            <Switch
              aria-label={s.title}
              checked={current[s.id]}
              disabled={!canEdit || !supported || saving}
              onCheckedChange={(on) => set(s.id, on)}
            />
          }
        />
      ))}
      {canEdit && !supported ? (
        <div className="px-4 py-3">
          <NeedsBackendNotice testId="case-insights-needs-backend" />
        </div>
      ) : null}
    </CasePrepSection>
  );
};
